import React from 'react';
import { ArrowLeft, Shield, Users, FileText, Lock, Mail, CheckCircle2 } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { appPath } from '../utils/appRouting';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="h-[100dvh] overflow-y-auto bg-gradient-to-b from-muted/30 to-background text-foreground scroll-smooth">
      {/* Header */}
      <header className="bg-background border-b border-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(appPath('/'))}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Torna alla Home</span>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                <span className="text-yellow-400 font-bold text-sm">S+</span>
              </div>
              <span className="text-sm font-semibold text-foreground hidden sm:block">SOSTEGNO PRO+</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {/* Title Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Informativa sulla Privacy</h1>
          <p className="text-muted-foreground">Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT')}</p>
        </div>

        {/* Content */}
        <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
          <div className="p-6 md:p-8 space-y-8">
            
            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">1</span>
                Titolare del Trattamento
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Il Titolare del trattamento dei dati personali è l'istituzione scolastica che utilizza la piattaforma 
                SOSTEGNO PRO+. La piattaforma agisce come responsabile del trattamento ai sensi dell'art. 28 del GDPR.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">2</span>
                Base Giuridica del Trattamento
              </h2>
              <p className="text-muted-foreground mb-3">Il trattamento dei dati personali si basa su:</p>
              <ul className="space-y-2">
                {[
                  'Adempimento di obblighi legali (D.Lgs. 66/2017 e successive modifiche)',
                  'Esecuzione di un compito di interesse pubblico (istruzione e inclusione scolastica)',
                  'Consenso esplicito degli interessati, ove necessario'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-muted-foreground">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">3</span>
                Dati Personali Raccolti
              </h2>
              
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">Dati degli Utenti</h3>
                      <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                        <li>Dati anagrafici (nome, cognome, email)</li>
                        <li>Ruolo professionale e qualifica</li>
                        <li>Credenziali di accesso (email e password criptata)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 rounded-xl p-5 border border-purple-100">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">Dati degli Studenti</h3>
                      <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                        <li>Dati anagrafici (nome, cognome, data di nascita, codice fiscale)</li>
                        <li>Dati relativi alla certificazione di disabilità (L.104/92)</li>
                        <li>Informazioni sul profilo di funzionamento e diagnosi</li>
                        <li>Dati relativi al Piano Educativo Individualizzato</li>
                        <li>Documentazione scolastica e verbali GLO</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">4</span>
                Finalità del Trattamento
              </h2>
              <div className="bg-muted/40 rounded-xl p-5 border border-border">
                <ul className="text-muted-foreground space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Gestione e redazione dei Piani Educativi Individualizzati (PEI)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Organizzazione e documentazione degli incontri del Gruppo di Lavoro Operativo (GLO)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Comunicazione tra docenti, dirigente scolastico e personale educativo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Gestione documentale e archiviazione digitale</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Adempimento degli obblighi normativi in materia di inclusione scolastica</span>
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">5</span>
                Modalità di Trattamento e Sicurezza
              </h2>
              <div className="bg-green-50 rounded-xl p-5 border border-green-100">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Lock className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm mb-3">
                      I dati personali sono trattati con strumenti informatici e telematici, con misure di sicurezza adeguate:
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                      <li>Crittografia dei dati sensibili</li>
                      <li>Autenticazione sicura degli utenti</li>
                      <li>Controllo degli accessi basato sui ruoli</li>
                      <li>Backup regolari e disaster recovery</li>
                      <li>Protocolli HTTPS per la trasmissione dei dati</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">6</span>
                Uso dell’Intelligenza Artificiale
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                I contenuti generati da SostegnoPro tramite intelligenza artificiale costituiscono esclusivamente bozze di supporto alla redazione dei documenti scolastici e non rappresentano documenti ufficiali né atti amministrativi. La verifica, l’adattamento, la validazione e la firma dei documenti restano di esclusiva responsabilità dell’utente.
              </p>
              <p className="text-muted-foreground mt-3">
                Per ulteriori dettagli, consulta le{' '}
                <Link to={appPath('/note-legali-ai')} className="text-blue-600 hover:underline font-medium">
                  Note legali – Uso dell’Intelligenza Artificiale
                </Link>
                .
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">7</span>
                Periodo di Conservazione
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                I dati personali sono conservati per il periodo necessario al conseguimento delle finalità per cui sono stati 
                raccolti e comunque non oltre i termini previsti dalla normativa vigente in materia di documentazione scolastica 
                (generalmente fino al compimento del 25° anno di età dello studente).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">8</span>
                Diritti degli Interessati
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  'Accedere ai propri dati (art. 15)',
                  'Rettifica dei dati (art. 16)',
                  'Cancellazione dei dati (art. 17)',
                  'Limitazione del trattamento (art. 18)',
                  'Opposizione al trattamento (art. 21)',
                  'Portabilità dei dati (art. 20)',
                  'Revoca del consenso',
                  'Reclamo al Garante Privacy'
                ].map((right, i) => (
                  <div key={i} className="bg-muted/40 rounded-lg p-3 text-sm text-muted-foreground flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    {right}
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">9</span>
                Cookie
              </h2>
              <p className="text-muted-foreground">
                Per informazioni sull'utilizzo dei cookie e altre tecnologie di tracciamento, consulta la nostra{' '}
                <Link to={appPath('/cookie-policy')} className="text-blue-600 hover:underline font-medium">
                  Cookie Policy
                </Link>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">10</span>
                Riferimenti Normativi
              </h2>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Regolamento UE 2016/679 (GDPR)</li>
                  <li>• D.Lgs. 196/2003 (Codice Privacy) modificato dal D.Lgs. 101/2018</li>
                  <li>• D.Lgs. 66/2017 (Norme per la promozione dell'inclusione scolastica)</li>
                  <li>• D.I. 182/2020 (Adozione del modello nazionale di PEI)</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">11</span>
                Contatti
              </h2>
              <div className="bg-muted/40 rounded-xl p-5 border border-border">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm mb-2">
                      Per esercitare i propri diritti o per informazioni sul trattamento dei dati:
                    </p>
                    <a href="mailto:privacy@sostegnopro.it" className="text-blue-600 hover:underline font-medium">
                      privacy@sostegnopro.it
                    </a>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
          <Link to={appPath('/note-legali-ai')} className="hover:text-blue-600 transition-colors">Note legali AI</Link>
          <Link to={appPath('/cookie-policy')} className="hover:text-blue-600 transition-colors">Cookie Policy</Link>
          <Link to={appPath('/terms')} className="hover:text-blue-600 transition-colors">Termini e Condizioni</Link>
          <Link to={appPath('/')} className="hover:text-blue-600 transition-colors">Home</Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
